import type { StoreLink } from "./types";
import { linkLabelMaxCharacters, maxStoreLinks } from "./content-limits";
import { lengthError, type FieldError } from "./field-validation";

const videoHostAllowlist = new Set([
  "youtube.com",
  "www.youtube.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
]);
const videoFileExtensions = [".mp4", ".webm", ".mov"];

export function isAllowedVideoUrl(url: string): boolean {
  if (!isAllowedLinkUrl(url)) return false;

  const parsed = new URL(url);
  // The backend embed allowlist compares the supplied authority, including any explicit port.
  const authority = url
    .slice("https://".length)
    .replace(/[/?#].*$/, "")
    .toLowerCase();

  if (videoHostAllowlist.has(authority)) return true;

  const path = parsed.pathname.toLowerCase();
  return videoFileExtensions.some((ext) => path.endsWith(ext));
}

export function isAllowedLinkUrl(url: string): boolean {
  if (!/^https:\/\//i.test(url) || url.length > 2000) return false;

  const parsed = URL.parse(url);

  if (!parsed) return false;

  const authority = url.slice("https://".length).replace(/[/?#].*$/, "");

  if (authority.includes("@")) return false;

  const host = parsed.hostname;

  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;

  if (host.startsWith("[") || host.includes(":")) return false;

  return true;
}

export function linkErrors(links: readonly StoreLink[]): FieldError[] {
  const errors: FieldError[] = [];

  if (links.length > maxStoreLinks) {
    errors.push({
      field: "links",
      message: `links: at most ${maxStoreLinks}`,
    });
  }

  links.forEach((link, i) => {
    const labelErr = lengthError({
      field: `links[${i}].label`,
      label: `link ${i + 1} label`,
      value: link.label,
      max: linkLabelMaxCharacters,
    });

    if (labelErr) errors.push(labelErr);

    if (!isAllowedLinkUrl(link.url)) {
      errors.push({
        field: `links[${i}].url`,
        message: `link ${i + 1}: https:// only — no credentials or IP-literal host`,
      });
    }
  });
  return errors;
}
